import { type ReactNode, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import { generateUuid } from '@witmetrics/utils/uuid';
import { isEmpty, isNullish } from '@witmetrics/utils/misc';
import type { Nullish } from '@witmetrics/utils';
import { isBlank } from '@witmetrics/utils/strings';
import { deepMerge } from '@witmetrics/utils/objects';
import type { SelectDropdownOption } from './types';
import { buildOption, classes, styles } from './utils';

export type { SelectDropdownOption };

export { buildOption };

export interface SelectDropdownProps<T extends string | number> {
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  menuClassName?: string;
  sx?: FormControlProps['sx'];
  variant?: 'outlined' | 'filled';
  label?: string;
  placeholder?: string;
  value: SelectDropdownOption<T> | Nullish;
  options?: Array<SelectDropdownOption<T>>;
  renderIcon?: (className: string) => ReactNode;
  onChange: (update: SelectDropdownOption<T>) => void;
}

export default function SelectDropdown<T extends string | number>({
  disabled,
  autoFocus,
  className,
  menuClassName,
  sx = {},
  variant = 'outlined',
  label,
  options = [],
  placeholder,
  value,
  renderIcon,
  onChange,
}: SelectDropdownProps<T>) {
  const idRef = useRef(generateUuid());

  const handleChange = (e: SelectChangeEvent<T>) => {
    const option = options.find((o) => o.value === e.target.value)!;
    onChange(option);
  };

  const getOption = (value: T) => {
    return options.find((o) => o.value === value)!;
  };

  const getLabels = (selected: T) => {
    if (isNullish(selected)) return [];
    return [getOption(selected).label];
  };

  const getValue = () => {
    if (isNullish(value)) return '';
    else return value.value;
  };

  const renderValue = (selected: T) => {
    if (value?.label && !isBlank(value.label)) return value.label;
    const labels = getLabels(selected);
    if (isEmpty(labels)) return '';
    return labels[0];
  };

  const labelId = `${idRef.current}-label`;

  return (
    <FormControl
      disabled={disabled}
      sx={deepMerge(styles[variant], sx || {})}
      className={className}
      size="small">
      {label && <InputLabel id={labelId}>{label}</InputLabel>}
      <Select<T>
        defaultOpen={!disabled && autoFocus}
        labelId={labelId}
        id={idRef.current}
        name={idRef.current}
        placeholder={placeholder}
        value={getValue()}
        MenuProps={{
          slotProps: {
            paper: { className: twMerge(classes.menu, menuClassName) },
          },
        }}
        input={
          <OutlinedInput
            id={`${idRef.current}-input`}
            label={label}
            startAdornment={
              value &&
              renderIcon && (
                <InputAdornment position="start">
                  {renderIcon(classes.inputIcon)}
                </InputAdornment>
              )
            }
          />
        }
        renderValue={renderValue}
        onChange={handleChange}>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            sx={styles.menuItem}
            className={classes.menuItem}
            value={option.value}>
            <div className={classes.start}>
              {option.startAdornment}
              {option.label}
            </div>
            {option.endAdornment}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
